import { useState, useEffect, useMemo } from 'react';
import { createCourseGroup, updateCourseGroup } from '../../services/content/courseGroupService';
import { searchCourses } from '../../services/content/courseService';
import type { CourseGroup, CourseGroupCourse } from '../../types/content/courseGroup';
import type { Course } from '../../types/content/course';

interface CourseGroupModalProps {
  courseGroup?: CourseGroup;
  onClose: () => void;
  onSuccess: () => void;
}

/**
 * Per-course entry in the modal's edit state. Mirrors CourseGroupCourse on the
 * wire; ordered position in this array drives display order within a category
 * (categoryOrder is written from index at save time).
 */
interface AssignmentRow {
  courseId: string;
  category: string;
  categoryOrder: number;
}

export function CourseGroupModal({ courseGroup, onClose, onSuccess }: CourseGroupModalProps) {
  const isEdit = !!courseGroup;

  const [name, setName] = useState(courseGroup?.name || '');
  const [courseCategory, setCourseCategory] = useState<string>(courseGroup?.courseCategory?.join(', ') || '');
  const [rows, setRows] = useState<AssignmentRow[]>(
    (courseGroup?.courseList ?? []).map((c: CourseGroupCourse) => ({
      courseId: c.courseId,
      category: c.category,
      categoryOrder: c.categoryOrder,
    }))
  );
  const [allCourses, setAllCourses] = useState<Course[]>([]);
  const [pickerFilter, setPickerFilter] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    searchCourses({}, { pageSize: 500, orderBy: 'name', order: 'asc' })
      .then(setAllCourses)
      .catch(() => {});
  }, []);

  const inputClass = 'w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm';
  const smallInputClass = 'px-2 py-1 border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-blue-500 text-sm';
  const labelClass = 'block text-sm font-medium text-gray-700 mb-1';
  const btnOutline = 'px-4 py-2 text-sm font-medium text-gray-700 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors';
  const btnPrimary = 'px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors';

  const categories = useMemo(
    () => courseCategory.split(',').map((c) => c.trim()).filter(Boolean),
    [courseCategory]
  );

  const coursesById = useMemo(() => {
    const map: Record<string, Course> = {};
    for (const c of allCourses) map[c.id] = c;
    return map;
  }, [allCourses]);

  const selectedIds = useMemo(() => new Set(rows.map((r) => r.courseId)), [rows]);

  const pickerCourses = useMemo(() => {
    const q = pickerFilter.trim().toLowerCase();
    return allCourses.filter((c) => {
      if (selectedIds.has(c.id)) return false;
      if (!q) return true;
      return c.name.toLowerCase().includes(q) || c.slug.toLowerCase().includes(q);
    });
  }, [allCourses, pickerFilter, selectedIds]);

  function addCourse(course: Course) {
    const defaultCategory = categories[0] ?? '';
    const inCategory = rows.filter((r) => r.category === defaultCategory).length;
    setRows((prev) => [
      ...prev,
      { courseId: course.id, category: defaultCategory, categoryOrder: inCategory + 1 },
    ]);
  }

  function removeCourse(courseId: string) {
    setRows((prev) => prev.filter((r) => r.courseId !== courseId));
  }

  function setRowCategory(courseId: string, category: string) {
    setRows((prev) => prev.map((r) => (r.courseId === courseId ? { ...r, category } : r)));
  }

  function moveRow(courseId: string, direction: -1 | 1) {
    setRows((prev) => {
      const idx = prev.findIndex((r) => r.courseId === courseId);
      if (idx < 0) return prev;
      const target = idx + direction;
      if (target < 0 || target >= prev.length) return prev;
      const next = [...prev];
      [next[idx], next[target]] = [next[target], next[idx]];
      return next;
    });
  }

  async function handleSave() {
    if (!name.trim()) return;
    setError('');
    setLoading(true);
    try {
      // Assign categoryOrder as position-within-category based on current row order.
      const perCategoryCount: Record<string, number> = {};
      const courseList: CourseGroupCourse[] = rows.map((r) => {
        perCategoryCount[r.category] = (perCategoryCount[r.category] ?? 0) + 1;
        return {
          courseId: r.courseId,
          category: r.category,
          categoryOrder: perCategoryCount[r.category],
        };
      });
      const data = {
        name: name.trim(),
        // Send only courseList — BE derives courseIds and courseCategory from it
        // in BeforeSave. Also send explicit courseCategory to control category
        // display order independent of first-appearance.
        courseCategory: categories,
        courseList,
      };
      if (isEdit) {
        await updateCourseGroup(courseGroup!.id, { ...courseGroup!, ...data });
      } else {
        await createCourseGroup(data);
      }
      onSuccess();
    } catch {
      setError(isEdit ? 'Failed to update course group' : 'Failed to create course group');
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div className="absolute inset-0 bg-black/40" onClick={onClose} />
      <div className="relative bg-white rounded-xl shadow-lg w-full max-w-2xl mx-4 max-h-[90vh] flex flex-col">
        <div className="px-6 pt-6 pb-4 overflow-y-auto flex-1">
          <h2 className="text-lg font-bold text-gray-900 mb-4">
            {isEdit ? 'Edit Course Group' : 'Add Course Group'}
          </h2>

          <div className="space-y-4">
            <div>
              <label className={labelClass}>Name *</label>
              <input type="text" value={name} onChange={(e) => setName(e.target.value)} className={inputClass} placeholder="Course group name" />
            </div>
            <div>
              <label className={labelClass}>Categories (display order)</label>
              <input
                type="text"
                value={courseCategory}
                onChange={(e) => setCourseCategory(e.target.value)}
                className={inputClass}
                placeholder="Comma-separated: Fundamentals, Intermediate, Advanced"
              />
              <p className="text-xs text-gray-500 mt-1">Left-to-right = top-to-bottom in the LMS. Each course below must be assigned to one of these categories.</p>
            </div>

            <div>
              <label className={labelClass}>Selected Courses ({rows.length})</label>
              {rows.length === 0 ? (
                <p className="px-3 py-2 text-sm text-gray-400 border border-dashed border-gray-200 rounded-lg text-center">
                  Add courses from the picker below.
                </p>
              ) : (
                <ul className="border border-gray-200 rounded-lg divide-y divide-gray-100">
                  {rows.map((r, idx) => {
                    const course = coursesById[r.courseId];
                    return (
                      <li key={r.courseId} className="flex items-center gap-2 px-3 py-2">
                        <div className="flex flex-col gap-0.5">
                          <button
                            type="button"
                            onClick={() => moveRow(r.courseId, -1)}
                            disabled={idx === 0}
                            className="text-xs text-gray-500 hover:text-gray-900 disabled:text-gray-300"
                            aria-label="Move up"
                          >▲</button>
                          <button
                            type="button"
                            onClick={() => moveRow(r.courseId, 1)}
                            disabled={idx === rows.length - 1}
                            className="text-xs text-gray-500 hover:text-gray-900 disabled:text-gray-300"
                            aria-label="Move down"
                          >▼</button>
                        </div>
                        <span className="flex-1 text-sm text-gray-700 truncate">
                          {course?.name ?? r.courseId}
                        </span>
                        <select
                          value={r.category}
                          onChange={(e) => setRowCategory(r.courseId, e.target.value)}
                          className={smallInputClass}
                        >
                          <option value="">(no category)</option>
                          {categories.map((c) => (
                            <option key={c} value={c}>{c}</option>
                          ))}
                          {r.category && !categories.includes(r.category) && (
                            <option value={r.category}>{r.category} (missing)</option>
                          )}
                        </select>
                        <button
                          type="button"
                          onClick={() => removeCourse(r.courseId)}
                          className="text-xs text-red-600 hover:text-red-800"
                          aria-label="Remove"
                        >✕</button>
                      </li>
                    );
                  })}
                </ul>
              )}
            </div>

            <div>
              <label className={labelClass}>Add Course</label>
              <input
                type="text"
                value={pickerFilter}
                onChange={(e) => setPickerFilter(e.target.value)}
                className={inputClass}
                placeholder="Search by name or slug..."
              />
              <div className="mt-2 border border-gray-200 rounded-lg max-h-48 overflow-y-auto divide-y divide-gray-100">
                {pickerCourses.length === 0 ? (
                  <p className="px-3 py-2 text-sm text-gray-400">
                    {allCourses.length === 0 ? 'Loading courses...' : 'No matches.'}
                  </p>
                ) : (
                  pickerCourses.map((c) => (
                    <button
                      key={c.id}
                      type="button"
                      onClick={() => addCourse(c)}
                      className="w-full text-left flex items-center gap-2 px-3 py-2 hover:bg-gray-50"
                    >
                      <span className="text-sm text-gray-700 flex-1 truncate">{c.name}</span>
                      <span className="text-xs text-gray-400">{c.slug}</span>
                    </button>
                  ))
                )}
              </div>
            </div>
          </div>

          {error && <p className="text-red-600 text-sm mt-3">{error}</p>}
        </div>

        <div className="flex items-center justify-end gap-3 px-6 py-4 border-t border-gray-100">
          <button type="button" onClick={onClose} className={btnOutline}>Cancel</button>
          <button type="button" onClick={handleSave} disabled={loading || !name.trim()} className={btnPrimary}>
            {loading ? 'Saving...' : 'Save'}
          </button>
        </div>
      </div>
    </div>
  );
}
